import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { injectStructuredData } from '@/components/feature/StructuredData';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [related, setRelated] = useState<Article[]>([]);

  useEffect(() => {
    if (slug && articleMap[slug]) {
      const art = articleMap[slug];
      setArticle(art);
      setRelated(getRelatedArticles(slug, art.category, 3));

      injectStructuredData([
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://peachhipguide.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: categoryMap[art.category]?.name || art.category,
              item: `https://peachhipguide.com/category/${art.category}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: art.title,
            },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: art.title,
          description: art.excerpt,
          author: {
            '@type': 'Person',
            name: art.author,
          },
          datePublished: art.date,
          inLanguage: 'zh-CN',
          url: `https://peachhipguide.com/article/${art.slug}`,
          image: art.coverImage,
        },
      ]);
    }
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-500 text-sm">文章未找到</p>
          <Link to="/" className="text-primary-600 text-sm mt-2 inline-block hover:underline">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const catName = categoryMap[article.category]?.name || article.category;

  return (
    <div className="min-h-screen pt-16">
      <article className="bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: catName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6 mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 蜜桃臀塑形指南`}
              className="w-full h-auto object-cover max-h-[450px]"
            />
          </div>

          <div
            className="prose-custom bg-background-50 rounded-lg border border-background-200/60 p-6 md:p-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <footer className="mt-10 pt-8 border-t border-background-200/60">
            {related.length > 0 && (
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {related.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group block bg-background-50 rounded-lg border border-background-200/60 overflow-hidden hover:border-primary-200 transition-all"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-foreground-900 font-medium text-xs leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </footer>
        </div>
      </article>
    </div>
  );
}